import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { useAddressAdditionalFields } from '@restworld/utils-common';
import { HomeIcon } from '@heroicons/react/24/outline';
import { GoogleAutoCompleteInput } from '@restworld/ui-ds-base';
import { Heading } from '@restworld/ui-ds-base';

import { SaveResearchFormFields } from '../job-explorer-save-research-v2';

interface IAddressInputProps {
  showAddressText?: boolean;
}

export const SaveResearchAddressInput = ({ showAddressText }: IAddressInputProps) => {
  const [address, setAddress] = useState<string>();
  const [modifyAddressFlag, toggleModifyAddressFlag] = useState(true);

  const { setValue, getValues, control } = useFormContext<SaveResearchFormFields>();
  // initialFields: the saved research address already carries lat/lon, so re-opening
  // the modal must not re-geocode it (billed call); only a new selection geocodes.
  const addressFields = useAddressAdditionalFields({
    addressString: address ?? '',
    initialFields: getValues('address') ?? undefined,
    surface: 'worker_save_research_address',
  });

  const { t } = useTranslation();

  useEffect(() => {
    if (addressFields && Object.keys(addressFields).length) {
      toggleModifyAddressFlag(false);
      setValue('address_lat', addressFields.address_lat);
      setValue('address_lon', addressFields.address_lon);
      setValue('address', {
        address: addressFields.address,
        country: addressFields.country,
        address_lat: addressFields.address_lat,
        address_lon: addressFields.address_lon,
        administrative_area_level_1: addressFields.administrative_area_level_1,
        administrative_area_level_2: addressFields.administrative_area_level_2,
      });
      if (addressFields.postal_code) setValue('postal_code', addressFields.postal_code);
      if (addressFields.locality) setValue('locality', addressFields.locality);
      setValue('street_number', addressFields.street_number);
    }
  }, [addressFields, setValue]);

  useEffect(() => {
    if (getValues('address')?.address) {
      setAddress(getValues('address').address);
    }
  }, [getValues]);

  return showAddressText && !modifyAddressFlag ? (
    <>
      {addressFields?.address ? (
        <span className="flex flex-row items-center gap-1.5">
          <HomeIcon className="h-6 w-6 text-gray-600" />
          <Heading>{addressFields?.address}</Heading>
        </span>
      ) : null}
      <a
        href="#!"
        className="text-primary underline text-sm font-semibold leading-[15.4px] max-w-fit"
        onClick={() => toggleModifyAddressFlag(true)}
      >
        Modifica
      </a>
    </>
  ) : (
    <GoogleAutoCompleteInput
      formId="research-address-input"
      trackingSurface="worker_save_research_address"
      key="research-address-input"
      placeholder={t('job-explorer-page:enter_address_or_location')}
      placesFilterTypes={['geocode']}
      onSuggestionSelect={({ description }) => {
        setAddress(description);
      }}
      control={control}
      // Desktop only: on phones focusing on mount pops the keyboard over the step before the user has
      // read it. Same breakpoint as the DS Modal (Dialog from 768px, bottom drawer below).
      autoFocus={typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches}
    />
  );
};

export default SaveResearchAddressInput;
